import axios from '@/core/axios';

import {
  Buyback,
  BuybackCheckMessage,
  BuybackCommissionRate,
  BuybackListOptions,
  BuybackReturnLabel,
  CounterOfferPayload,
  CounterOfferReasonGroup,
  SuspendReason,
  TabCount,
  UrgentCount
} from './buybacks.types';
import { CollectionList } from '../collections/collections.types';

export class BuybackService {
  async list(opts: BuybackListOptions = {}): Promise<CollectionList<'buybacks'>> {
    const { filters, pagination } = opts;
    const resp = await axios.get<CollectionList<'buybacks'>>('/buybacks', { params: { ...filters, ...pagination } });
    return resp?.data;
  }

  async update(id: number, buyback: Partial<Buyback>): Promise<Buyback> {
    if (!id) throw new Error('Missing buyback id in update');
    const resp = await axios.put<Buyback>(`/buybacks/${id}`, { buyback });
    return resp?.data;
  }

  async get(id: number): Promise<Buyback> {
    const resp = await axios.get<Buyback>(`/buybacks/${id}`);
    return resp?.data;
  }

  async delete(id: number): Promise<void> {
    if (!id) throw new Error('Missing buyback id in delete');
    await axios.delete(`/buybacks/${id}`);
  }

  async printLabel(id: number): Promise<string> {
    const resp = await axios.get<{ zpl: string }>(`/buybacks/${id}/print-label`);
    return resp?.data?.zpl;
  }

  async checkBuyback(mpBuybackId: string): Promise<BuybackCheckMessage> {
    const resp = await axios.get<BuybackCheckMessage>(`/buybacks/${mpBuybackId}/check-buyback`);
    return resp?.data;
  }

  async validateBuyback(id: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${id}/validate`);
    return resp?.data;
  }

  async counterOfferBuyback(mpBuybackId: string, payload: CounterOfferPayload): Promise<Buyback> {
    const resp = await axios.put<Buyback>(`/buybacks/${mpBuybackId}/counter-offer`, payload);
    return resp?.data;
  }

  async getCounterOfferReasons(mpBuybackId: string): Promise<CounterOfferReasonGroup[]> {
    const resp = await axios.get<CounterOfferReasonGroup[]>(`/buybacks/${mpBuybackId}/counter-offer-reasons`);
    return resp?.data;
  }

  async getSuspendReasons(mpBuybackId: string): Promise<SuspendReason[]> {
    const resp = await axios.get<SuspendReason[]>(`/buybacks/${mpBuybackId}/suspend-reasons`);
    return resp?.data;
  }

  async suspendBuyback(mpBuybackId: string, reasons: string[]): Promise<Buyback> {
    const resp = await axios.put<Buyback>(`/buybacks/${mpBuybackId}/suspend`, { reasons });
    return resp?.data;
  }

  async generateRmaFile(buybackId: number, force = false): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/rma-file`, { force });
    return resp?.data;
  }

  async cancelBuyback(buybackId: number, serialNumber?: string, cancellationFile?: File): Promise<Buyback> {
    if (cancellationFile) {
      const formData = new FormData();
      formData.append('serial_no', serialNumber || '');
      formData.append('cancellation_file', cancellationFile);

      const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/cancel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return resp?.data;
    }

    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/cancel`, { serial_no: serialNumber || null });
    return resp?.data;
  }

  async getReturnLabel(buybackId: number): Promise<BuybackReturnLabel> {
    const resp = await axios.get<BuybackReturnLabel>(`/buybacks/${buybackId}/return-label`);
    return resp?.data;
  }

  async getByDeviceId(deviceId: number): Promise<Buyback> {
    const resp = await axios.get<Buyback>(`/buybacks/by-device/${deviceId}`);
    return resp?.data;
  }

  async updateSuspendedStep(buybackId: number, step: string): Promise<Buyback> {
    const resp = await axios.put<Buyback>(`/buybacks/${buybackId}/suspended-step`, { step });
    return resp?.data;
  }

  async handToShipping(buybackId: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/hand-to-shipping`);
    return resp?.data;
  }

  async declareComputerReception(buybackId: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/computer-reception`);
    return resp?.data;
  }

  async markShipped(buybackId: number, manualTrackingNumber: string): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/mark-shipped`, {
      manual_tracking_number: manualTrackingNumber
    });
    return resp?.data;
  }

  async getUrgentCount(): Promise<UrgentCount> {
    const resp = await axios.get<UrgentCount>('/buybacks/urgent-count');
    return resp?.data;
  }

  async getTabCounts(): Promise<TabCount> {
    const resp = await axios.get<TabCount>('/buybacks/tab-counts');
    return resp?.data;
  }

  async acquireBuyback(buybackId: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/acquire`);
    return resp?.data;
  }

  async declareSupplierReturn(buybackId: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/return-supplier`);
    return resp?.data;
  }

  async supplierResponse(buybackId: number): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/supplier-response`);
    return resp?.data;
  }

  async getByTrackingNumber(trackingNumber: string): Promise<{ buybacks: Buyback[]; message?: string }> {
    const resp = await axios.get<{ buybacks: Buyback[]; message?: string }>(
      `/buybacks/by-tracking/${encodeURIComponent(trackingNumber)}`
    );
    return resp?.data;
  }

  async declareReception(buybackId: number, reservation = false): Promise<Buyback> {
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/reception`, {
      reservation
    });
    return resp?.data;
  }

  async uploadSkuMpConfig(file: File): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await axios.post<{ message: string }>('/buybacks/sku-mp-config/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp?.data;
  }

  async addReservePhoto(buybackId: number, photo: File): Promise<Buyback> {
    if (!buybackId) throw new Error('Missing buyback id in addReservePhoto');
    const formData = new FormData();
    formData.append('buyback[photos]', photo);
    const resp = await axios.put<Buyback>(`/buybacks/${buybackId}/photos/reservation`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp?.data;
  }

  async deleteReservePhoto(buybackId: number, photoId: number): Promise<Buyback> {
    if (!buybackId) throw new Error('Missing buyback id in deleteReservePhoto');
    const resp = await axios.delete<Buyback>(`/buybacks/${buybackId}/photos/reservation/${photoId}`);
    return resp?.data;
  }

  async addSuspensionPhoto(buybackId: number, photo: File): Promise<Buyback> {
    if (!buybackId) throw new Error('Missing buyback id in addSuspensionPhoto');
    const formData = new FormData();
    formData.append('buyback[photos]', photo);
    const resp = await axios.put<Buyback>(`/buybacks/${buybackId}/photos/suspension`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp?.data;
  }

  async deleteSuspensionPhoto(buybackId: number, photoId: number): Promise<Buyback> {
    if (!buybackId) throw new Error('Missing buyback id in deleteSuspensionPhoto');
    const resp = await axios.delete<Buyback>(`/buybacks/${buybackId}/photos/suspension/${photoId}`);
    return resp?.data;
  }

  async getCommissionRate(): Promise<number> {
    const resp = await axios.get<BuybackCommissionRate>('/buybacks/commission-rate');
    return resp?.data?.commission_rate;
  }

  async updateCommissionRate(commissionRate: number | string): Promise<number> {
    const resp = await axios.put<BuybackCommissionRate>('/buybacks/commission-rate', {
      commission_rate: commissionRate
    });
    return resp?.data?.commission_rate;
  }

  async moveBuyback(buybackId: number, step: string): Promise<Buyback> {
    if (!buybackId) throw new Error('Missing buyback id in moveBuyback');
    if (!step) throw new Error('Missing step in moveBuyback');
    const resp = await axios.post<Buyback>(`/buybacks/${buybackId}/move`, { step });
    return resp?.data;
  }
}
export const buybackService = new BuybackService();
